import React, { useState, useEffect, useMemo } from 'react';
import {
  Table,
  Button,
  Tag,
  Input,
  Select,
  Form,
  Space,
  Tooltip,
  Popconfirm,
  Tabs,
  Empty,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  SearchOutlined, ReloadOutlined, TagsOutlined,
  UserOutlined, CarOutlined, EnvironmentOutlined,
  HomeOutlined, PushpinOutlined,
} from '@ant-design/icons';
import Layout from '../../components/Layout';
import axiosInstance from '../../variables/api/axiosInstance';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { avecLibelleTraduit } from '../../i18n/libelles';
import CBModal from '../../components/CBModal';
import PageHeader from '../../components/PageHeader';

// ── Catégories connues ─────────────────────────────────────────────
const CATEGORIES = avecLibelleTraduit([
  { key: 'PROFESSION', icon: <UserOutlined />, color: 'blue' },
  { key: 'MARQUE', icon: <CarOutlined />, color: 'orange' },
  { key: 'VILLE', icon: <EnvironmentOutlined />, color: 'green' },
  { key: 'QUARTIER', icon: <HomeOutlined />, color: 'purple' },
  { key: 'COMMUNE', icon: <PushpinOutlined />, color: 'magenta' },
], 'parametres:listes.categories', { cle: 'key' });

// Extrait la catégorie et le code depuis la clé "CATEGORIE.CODE"
const parseKey = (cle) => {
  const idx = cle?.indexOf('.');
  if (idx < 0) return { categorie: cle, code: '' };
  return { categorie: cle.substring(0, idx), code: cle.substring(idx + 1) };
};

const buildKey = (categorie, code) =>
  `${categorie.toUpperCase()}.${code.toUpperCase().replace(/\s+/g, '_')}`;

export default function ParametresListe() {
  const { t } = useTranslation('parametres');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('PROFESSION');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const load = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/v1/parametres?type=LISTE');
      setData(res.data?.data || []);
    } catch { toast.error(i18n.t('parametres:listes.messages.erreurChargement')); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const matchesCategorie = (cle, tab) => {
    const { categorie } = parseKey(cle);
    return categorie === tab || categorie.startsWith(`${tab}_`);
  };

  const filtered = useMemo(() => {
    return data
      .filter(p => matchesCategorie(p.cle, activeTab))
      .filter(p =>
        !search ||
        p.valeur?.toLowerCase().includes(search.toLowerCase()) ||
        p.description?.toLowerCase().includes(search.toLowerCase())
      );
  }, [data, activeTab, search]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ categorie: activeTab });
    setModalOpen(true);
  };

  const openEdit = (record) => {
    const { categorie, code } = parseKey(record.cle);
    setEditing(record);
    form.setFieldsValue({ categorie, code, valeur: record.valeur, description: record.description });
    setModalOpen(true);
  };

  /**
   * Le code interne n'est plus demande a l'utilisateur : « lettres majuscules, sans
   * espaces » est une contrainte technique, pas une information metier, et on ne
   * connait pas toujours le code d'une profession qu'on ajoute a la volee.
   *
   * Il reste OBLIGATOIRE cote base - il compose la cle unique du parametre. On le
   * derive donc du libelle quand il est vide : « Chef d'atelier » -> CHEF_D_ATELIER.
   * Saisi a la main, il est respecte tel quel.
   */
  const deriverCode = (libelle) => (libelle || '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')   // accents
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);

  const onSubmit = async (vals) => {
    const code = (vals.code || '').trim() || deriverCode(vals.valeur);
    if (!code) {
      toast.error(t('listes.messages.libelleRequis'));
      return;
    }
    const cle = buildKey(vals.categorie, code);
    const payload = {
      typeParametre: 'LISTE',
      cle,
      valeur: vals.valeur.trim(),
      description: vals.description?.trim() || null,
    };
    try {
      if (editing) {
        await axiosInstance.put(`/v1/parametres/${editing.parametreTrackingId}`, payload);
        toast.success(t('listes.messages.parametreModifie'));
      } else {
        await axiosInstance.post('/v1/parametres', payload);
        toast.success(t('listes.messages.parametreCree'));
      }
      setModalOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('listes.messages.erreurSauvegarde'));
    }
  };

  const supprimer = async (record) => {
    try {
      await axiosInstance.delete(`/v1/parametres/${record.parametreTrackingId}`);
      toast.success(t('listes.messages.parametreSupprime'));
      load();
    } catch { toast.error(t('listes.messages.erreurSuppression')); }
  };

  const cols = [
    {
      title: t('listes.colonnes.libelle'), dataIndex: 'valeur', key: 'val', width: 240,
      render: (v) => <span style={{ fontWeight: 600 }}>{v}</span>,
    },
    {
      title: t('listes.colonnes.code'), dataIndex: 'cle', key: 'cle', width: 260,
      render: (v) => {
        const { categorie, code } = parseKey(v);
        const variante = categorie.includes('_') ? categorie : null;
        return (
          <span style={{ fontFamily: 'var(--font-main)', fontSize: 12, color: '#6b7280' }}>
            {code}
            {variante ? (
              <Tag style={{ marginLeft: 8, fontSize: 10, borderRadius: 10 }} color="geekblue">
                {variante}
              </Tag>
            ) : null}
          </span>
        );
      },
    },
    {
      title: t('listes.colonnes.description'), dataIndex: 'description', key: 'desc',
      ellipsis: true,
      render: (v) => v ? <span style={{ color: '#6b7280', fontSize: 13 }}>{v}</span> : '-',
    },
    {
      title: '', key: 'actions', width: 90, align: 'center',
      render: (_, r) => (
        <Space size={4}>
          <Tooltip title={t('listes.actions.modifier')}>
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}
              style={{ borderRadius: 6 }} />
          </Tooltip>
          <Popconfirm title={t('listes.actions.supprimerConfirmation')} onConfirm={() => supprimer(r)}
            okText={t('listes.actions.oui')} cancelText={t('listes.actions.non')}>
            <Tooltip title={t('listes.actions.supprimer')}>
              <Button size="small" icon={<DeleteOutlined />} danger style={{ borderRadius: 6 }} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const tabItems = CATEGORIES.map(cat => ({
    key: cat.key,
    label: (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        {cat.icon}
        {cat.label}
        <Tag color={cat.color} style={{ marginLeft: 4, borderRadius: 20, fontSize: 11 }}>
          {data.filter(p => matchesCategorie(p.cle, cat.key)).length}
        </Tag>
      </span>
    ),
    children: (
      <>
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
          <Input
            prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
            placeholder={t('listes.filtres.rechercher', { categorie: cat.label })}
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ maxWidth: 320, borderRadius: 8 }}
            allowClear
          />
        </div>

        {filtered.length === 0 ? (
          <Empty description={t('listes.tableau.aucuneEntree')} style={{ padding: 32 }} />
        ) : (
          <Table
            className="cb-table"
            dataSource={filtered.map((r, i) => ({ ...r, key: i }))}
            columns={cols}
            size="small"
            pagination={{ pageSize: 20, showTotal: (total) => t('listes.tableau.total', { count: total }) }}
          />
        )}
      </>
    ),
  }));

  return (
    <Layout title={t('listes.page.titreOnglet')}>
      <div>

        {/* En-tête */}
        {/* Le compte suit l'onglet et la recherche : `data` melange toutes les
            categories, seul `filtered` correspond a la liste sous les yeux. */}
        <PageHeader
          titre={t('listes.page.titre')}
          icone={<TagsOutlined />}
          compte={filtered.length}
          unite={t('listes.page.unite')}
          unitePluriel={t('listes.page.unitePluriel')}
          actions={(
            <Space>
              <Tooltip title={t('listes.actions.actualiser')}>
                <Button icon={<ReloadOutlined />} onClick={load} style={{ borderRadius: 8 }} />
              </Tooltip>
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}
                style={{ background: '#1a6b3c', borderColor: '#1a6b3c', borderRadius: 8 }}>
                {t('listes.actions.ajouter')}
              </Button>
            </Space>
          )}
        />

        {/* Onglets par catégorie */}
        <div className="cb-card" style={{ padding: 0 }}>
          <div style={{
            padding: '14px 20px 0', borderBottom: '1px solid #f3f4f6',
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <TagsOutlined style={{ color: '#1a6b3c' }} />
            <span style={{ fontWeight: 700, color: '#1a6b3c', fontSize: 13 }}>
              {t('listes.page.listesParametrables')}
            </span>
          </div>
          <div style={{ padding: '0 12px 16px' }}>
            <Tabs
              activeKey={activeTab}
              onChange={(k) => { setActiveTab(k); setSearch(''); }}
              items={tabItems}
            />
          </div>
        </div>

        {/* Modal création / édition */}
        <CBModal
          title={
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              {editing ? <EditOutlined /> : <PlusOutlined />}
              {editing ? t('listes.formulaire.titreModifier') : t('listes.formulaire.titreNouvelle')}
            </span>
          }
          open={modalOpen}
          onCancel={() => setModalOpen(false)}
          footer={null}
          width={480}
        >
          <Form form={form} layout="vertical" onFinish={onSubmit} style={{ marginTop: 16 }}>

            <Form.Item name="categorie" label={t('listes.formulaire.categorie')} rules={[{ required: true }]}>
              <Select
                disabled={!!editing}
                style={{ borderRadius: 8 }}
                getPopupContainer={t => t.parentElement}
                options={CATEGORIES.map(c => ({
                  value: c.key,
                  label: (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      {c.icon} {c.label}
                    </span>
                  ),
                }))}
              />
            </Form.Item>

            <Form.Item
              name="code"
              label={t('listes.formulaire.codeInterne')}
              rules={[
                { pattern: /^[A-Z0-9_]+$/, message: t('listes.formulaire.codeFormat') },
              ]}
            >
              <Input
                placeholder={t('listes.formulaire.codeExemple')}
                disabled={!!editing}
                style={{ borderRadius: 8, fontFamily: 'var(--font-main)' }}
                onChange={e => form.setFieldValue('code', e.target.value.toUpperCase().replace(/\s+/g, '_'))}
              />
            </Form.Item>

            <Form.Item name="valeur" label={t('listes.formulaire.libelleAffiche')}
              rules={[{ required: true, message: t('listes.formulaire.libelleObligatoire') }]}>
              <Input placeholder={t('listes.formulaire.libelleExemple')} style={{ borderRadius: 8 }} />
            </Form.Item>

            <Form.Item name="description" label={t('listes.formulaire.description')}>
              <Input placeholder={t('listes.formulaire.descriptionExemple')} style={{ borderRadius: 8 }} />
            </Form.Item>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button onClick={() => setModalOpen(false)}>{t('listes.formulaire.annuler')}</Button>
              <Button type="primary" htmlType="submit"
                style={{ background: '#1a6b3c', borderColor: '#1a6b3c', borderRadius: 8 }}>
                {editing ? t('listes.formulaire.modifier') : t('listes.formulaire.creer')}
              </Button>
            </div>
          </Form>
        </CBModal>
      </div>
    </Layout>
  );
}